
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Shield, 
  AlertTriangle, 
  Clock, 
  RefreshCw,
  Ban,
  Eye,
  Activity
} from 'lucide-react';
import { CertificateSecurityService } from '@/services/certificateSecurityService';
import { toast } from '@/components/ui/sonner';

interface SecurityIncident {
  id: string;
  userId: string;
  type: 'rate_limit' | 'authentication_failure' | 'suspicious_pattern' | 'duplicate_request';
  details: any;
  timestamp: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

const SecurityMonitoringPanel: React.FC = () => {
  const [securityStats, setSecurityStats] = useState<any>({
    totalIncidents: 0,
    rateLimitViolations: 0,
    authFailures: 0,
    suspiciousPatterns: 0,
    blockedUsers: 0
  });
  const [recentIncidents, setRecentIncidents] = useState<SecurityIncident[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSecurityData();
    // Set up periodic refresh for real-time monitoring
    const interval = setInterval(loadSecurityData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadSecurityData = async () => {
    setLoading(true);
    try {
      // In a real implementation, these would be actual security service calls
      // For now, we'll simulate the data structure
      const mockStats = {
        totalIncidents: Math.floor(Math.random() * 50),
        rateLimitViolations: Math.floor(Math.random() * 20),
        authFailures: Math.floor(Math.random() * 15),
        suspiciousPatterns: Math.floor(Math.random() * 10),
        blockedUsers: Math.floor(Math.random() * 5)
      };

      const mockIncidents: SecurityIncident[] = Array.from({ length: 10 }, (_, i) => ({
        id: `incident-${i}`,
        userId: `user-${Math.floor(Math.random() * 1000)}`,
        type: ['rate_limit', 'authentication_failure', 'suspicious_pattern', 'duplicate_request'][Math.floor(Math.random() * 4)] as any,
        details: { attemptCount: Math.floor(Math.random() * 10) + 1 },
        timestamp: new Date(Date.now() - Math.random() * 86400000).toISOString(),
        severity: ['low', 'medium', 'high', 'critical'][Math.floor(Math.random() * 4)] as any
      }));

      setSecurityStats(mockStats);
      setRecentIncidents(mockIncidents);
    } catch (error) {
      console.error('Error loading security data:', error);
      toast.error('Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUser = async (userId: string) => {
    try {
      // In real implementation, this would call CertificateSecurityService
      toast.success(`User ${userId} has been temporarily blocked`);
      await loadSecurityData();
    } catch (error) {
      console.error('Error blocking user:', error);
      toast.error('Failed to block user');
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const getIncidentIcon = (type: string) => {
    switch (type) {
      case 'rate_limit': return <Clock className="h-4 w-4" />;
      case 'authentication_failure': return <Shield className="h-4 w-4" />;
      case 'suspicious_pattern': return <AlertTriangle className="h-4 w-4" />;
      case 'duplicate_request': return <Activity className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p>Loading security data...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Security Alert Summary */}
      {securityStats.totalIncidents > 20 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            High security activity detected: {securityStats.totalIncidents} incidents in the last 24 hours.
            Consider reviewing certificate generation policies.
          </AlertDescription>
        </Alert>
      )}

      {/* Security Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Incidents</p>
                <p className="text-2xl font-bold">{securityStats.totalIncidents}</p>
              </div>
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Rate Limits</p>
                <p className="text-2xl font-bold text-orange-600">{securityStats.rateLimitViolations}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Auth Failures</p>
                <p className="text-2xl font-bold text-red-600">{securityStats.authFailures}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Suspicious Patterns</p>
                <p className="text-2xl font-bold text-purple-600">{securityStats.suspiciousPatterns}</p>
              </div>
              <Eye className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Blocked Users</p>
                <p className="text-2xl font-bold text-red-700">{securityStats.blockedUsers}</p>
              </div>
              <Ban className="h-8 w-8 text-red-700" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Security Incidents */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent Security Incidents</CardTitle>
          <Button onClick={loadSecurityData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentIncidents.map(incident => (
              <div key={incident.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  {getIncidentIcon(incident.type)}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={getSeverityColor(incident.severity) as any}>
                        {incident.severity}
                      </Badge>
                      <span className="font-medium">
                        {incident.type.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      User: {incident.userId} • {new Date(incident.timestamp).toLocaleString()}
                    </p>
                    <p className="text-sm">
                      {incident.details.attemptCount && `${incident.details.attemptCount} attempts`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleBlockUser(incident.userId)}
                  >
                    <Ban className="h-3 w-3 mr-1" />
                    Block
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Eye className="h-3 w-3 mr-1" />
                    Details
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityMonitoringPanel;
